import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";

const PROCESSING_STATUSES = ["PENDING", "EXTRACTING_TEXT", "BEING_MARKED"];

export const RetryExtractionButton = ({
  answer,
  onSuccess,
  disabled = false,
}) => {
  const ApiClient = useApiClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const allowance = answer?.submissionAllowance;
  const processing = PROCESSING_STATUSES.includes(answer?.status);
  const retryDisabled =
    disabled || processing || retrying || allowance?.allowed === false;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const response = await ApiClient.assignment.retryAnswerExtraction({
        answer: answer.id,
      });
      if (response?.success) {
        enqueueSnackbar("Re-extraction started", {
          variant: "success",
          autoHideDuration: 3000,
        });
        setConfirmOpen(false);
        await onSuccess?.();
      } else {
        enqueueSnackbar(response?.message || "Failed to start re-extraction", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to retry extraction", error);
      enqueueSnackbar("Failed to start re-extraction", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setRetrying(false);
    }
  };

  const allowanceText = allowance?.unlimited
    ? "Unlimited resubmissions available"
    : allowance?.remaining != null
      ? `${allowance.remaining} resubmission${allowance.remaining === 1 ? "" : "s"} remaining`
      : null;

  return (
    <>
      <Stack spacing={0.5} alignItems="flex-start">
        <Button
          variant="outlined"
          onClick={() => setConfirmOpen(true)}
          disabled={retryDisabled}
        >
          {retrying ? "Retrying..." : "Retry extraction"}
        </Button>
        {allowanceText && (
          <Typography variant="caption" color="text.secondary">
            {allowanceText}
          </Typography>
        )}
      </Stack>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Retry extraction?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Retrying extraction counts as a resubmission.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={retrying}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRetry} disabled={retrying}>
            {retrying ? "Retrying..." : "Retry extraction"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
